/**
 * useDraftState — draft-state primitive for SSE-exposed editors.
 *
 * Implements the "apply seed exactly once per entity identity; ignore
 * identity-preserving data churn" contract from the draft-state design doc
 * (plans/2026-06-11-006-draft-state-refactor-design.md §3.2).
 *
 * ## Why this hook exists
 *
 * Every editing surface inside Terra's v4 tree receives parent re-renders
 * whenever any `artifact:updated` SSE event lands — even for unrelated
 * artifacts. On an active account this happens every few seconds. Without
 * explicit guard logic, a `useEffect` seeded from `query.data` will re-run
 * on every cache identity change, wiping the user's in-progress edits.
 *
 * This hook centralises that guard so individual editors don't hand-roll it.
 *
 * ## State shape
 *
 * ```
 * { appliedKey, draft, pristine }
 * ```
 *
 * - `appliedKey` — the seedKey that was last applied. Compared against the
 *   current `seedKey` in the render phase; mismatch + defined seed triggers a
 *   reset.
 * - `draft` — the user-editable copy. Set via `setDraft`.
 * - `pristine` — the last server-ack'd value. Used to derive `isDirty`.
 *   Updated only by the initial seed and by `reseed(value)`.
 *
 * ## Render-phase reset
 *
 * The reseed-on-key-change logic runs in the render phase, not in a
 * `useEffect`. This is React's documented "adjust state during render" /
 * derived-state pattern (see https://react.dev/reference/react/useState
 * "Storing information from previous renders"). React aborts the current
 * render, re-renders with the new state, and the caller sees the new draft
 * without a double-render observable externally. The critical property: a
 * `setS` call during render only fires when `appliedKey !== seedKey`, so
 * there is no infinite loop — after the re-render `appliedKey === seedKey`
 * and the guard does not fire again.
 *
 * Same-key data-identity churn (SSE cache patches that produce a new object
 * with the same logical values) never triggers a reseed, by construction:
 * the guard is keyed on `seedKey`, not on `seed` identity.
 *
 * ## isDirty — shallow-equal decision
 *
 * `isDirty` is derived via a shallow-equal comparison of `draft` vs
 * `pristine` (one level of own enumerable properties). Skill/persona drafts
 * are flat objects (string / boolean / string-array fields). Array fields
 * (e.g. `requiresTools`) are compared by reference at the top level — a new
 * array with the same elements is considered dirty. This is consistent with
 * how React state updates work (you always produce a new array when mutating
 * array fields) and is intentionally strict: call `reseed` after a successful
 * save to clear the dirty flag with the server-normalized values.
 *
 * If callers need deeper equality for nested objects, they should pass a
 * custom `isDirty` predicate (a future extension; not implemented here).
 *
 * ## Primitive-value drafts (T = string | number | boolean)
 *
 * `T` does not have to be an object. Plain-text editors (e.g. `NotesEditor`)
 * use `useDraftState<string>`. `setDraft` detects whether the CURRENT draft
 * is a mergeable object (`typeof draft === 'object' && draft !== null`); if
 * not — primitive or still unseeded — it replaces the draft wholesale
 * instead of shallow-merging. This matters because `Partial<string>` is
 * structurally satisfied by a plain `string` (TS's `Partial` maps over
 * `keyof string`, which resolves to `String.prototype`'s members — a string
 * primitive already has all of them via autoboxing), so `setDraft(newValue)`
 * for a string draft type-checks with no cast. Without the object/primitive
 * branch, the object-spread merge (`{ ...prev.draft, ...partial }`) would
 * spread a string into an indexed object (`{ ...'Hi' }` → `{ 0: 'H', 1: 'i' }`),
 * corrupting the draft on the first keystroke — see the "primitive draft
 * values" tests below and PR #1046 review finding B1. `reseed` and
 * `reseedIfUnchanged` were never affected — they always replace wholesale.
 *
 * ## opts.initial — isNew path
 *
 * Editors that create new entities (e.g. `/skills/new`) have no server data.
 * Pass `opts.initial` to seed the draft before any server value arrives.
 * Rules:
 * - When `seed` is `undefined` and `initial` is defined: seed from `initial`.
 * - When `seed` is defined: server data always wins over `initial`.
 * - `opts.initial` is only applied once at mount — it is NOT reactive.
 *
 * ### Footgun: `opts.initial` + non-null `seedKey` at mount
 *
 * If `opts.initial` is provided AND `seedKey` is non-null at mount, the hook
 * pre-sets `appliedKey = seedKey` at initialization time. When `seed` later
 * becomes defined (server data loads), the render-phase guard sees
 * `appliedKey === seedKey` and **does nothing** — the server seed is silently
 * blocked. The draft stays on the initial value forever, even after data loads.
 *
 * The sanctioned pattern for "isNew" editors is:
 *
 * ```ts
 * // CORRECT: only pass opts.initial when there is no server key yet
 * const form = useDraftState(seed, id, isNew ? { initial: EMPTY_DRAFT } : undefined);
 * ```
 *
 * When `isNew` is true, `seed` is `undefined` and `id` is typically `null` or
 * a temporary placeholder — so the render-phase guard fires when the real id
 * and data arrive. When `isNew` is false (editing an existing entity), omit
 * `opts.initial` so the normal seed-on-first-key path applies.
 *
 * ## Undefined-seed deferral
 *
 * When `seed` is `undefined` and no `opts.initial` is provided, the hook
 * stays unseeded (`draft === undefined`). When `seed` becomes defined for
 * the current `seedKey`, it applies exactly once.
 */

import { useState, useCallback, type Key } from 'react';

// Types

export interface UseDraftStateOpts<T> {
  /**
   * Initial draft value for "isNew" paths (e.g. /skills/new).
   * Applied only when `seed` is `undefined`.
   * Server data (`seed`) always wins when both are provided.
   */
  initial?: T;
}

export interface UseDraftStateResult<T> {
  /** The user-editable draft. Undefined until seeded (or opts.initial). */
  draft: T | undefined;
  /**
   * Merge a partial update into the draft.
   *
   * Object drafts: shallow-merges `partial` over the current draft (or over
   * `{}` when unseeded), same as before.
   *
   * Primitive drafts (T = string | number | boolean): replaces the draft
   * wholesale instead of merging. This is required — `Partial<string>`
   * structurally accepts a plain `string` (TS's mapped-type machinery over
   * primitives resolves to an object type that a string satisfies via
   * String.prototype), so nothing in the type system stops a caller from
   * doing `setDraft(e.target.value)` for a string draft. Object-spreading a
   * string (`{ ...'Hi' }`) produces `{ 0: 'H', 1: 'i' }`, not a string — see
   * the "primitive draft values" tests in useDraftState.test.tsx.
   */
  setDraft: (partial: Partial<T>) => void;
  /**
   * Server-ack path. Sets both draft AND pristine, clearing isDirty.
   * Call in mutation `onSuccess` to adopt the server-normalized value.
   */
  reseed: (value: T) => void;
  /**
   * Snapshot-guard reseed. Only applies the reseed if the CURRENT draft
   * still shallow-equals `snapshot` (the draft captured at dispatch time).
   *
   * Use in mutation `onSuccess` to protect against keystrokes typed between
   * `mutate()` and the server response:
   *
   * ```ts
   * // In mutationFn or onMutate: capture the draft at dispatch time.
   * const dispatchedDraft = form.draft;
   *
   * // In onSuccess: only reseed when the user hasn't typed since dispatch.
   * form.reseedIfUnchanged(toDraft(data), dispatchedDraft);
   * ```
   *
   * If the user has typed since dispatch (`draft !== snapshot`), the draft is
   * kept (the newer in-flight edits are preserved) but `pristine` still adopts
   * the server value, so `isDirty` compares the in-flight edit against what the
   * server now holds.
   * The cache write (`queryClient.setQueryData`) and any navigation/toast
   * should still run unconditionally; only the reseed call becomes guarded.
   */
  reseedIfUnchanged: (value: T, snapshot: T | undefined) => void;
  /** True when draft !== pristine (shallow-equal comparison). */
  isDirty: boolean;
}

// Internal state shape

// Sentinel value used to mark "has not been seeded yet". Using a unique
// symbol ensures it can never collide with a real React.Key value
// (string | number | bigint | boolean).
const UNSEEDED: unique symbol = Symbol('UNSEEDED');
type SeedMarker = typeof UNSEEDED;

interface DraftStateInternal<T> {
  /** The seedKey that was last applied. UNSEEDED before any seed. */
  appliedKey: Key | null | SeedMarker;
  /** User-editable draft copy. */
  draft: T | undefined;
  /** Server-ack'd pristine value. Used to derive isDirty. */
  pristine: T | undefined;
}

// shallowEqual helper

/**
 * Shallow-equal: true iff both values are the same reference OR both are
 * plain objects with identical own enumerable keys and same-reference values.
 * Returns true for identical primitives and same-reference non-plain values.
 */
function shallowEqual<T>(a: T, b: T): boolean {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    a === null ||
    b === null
  ) {
    return false;
  }
  const keysA = Object.keys(a as object) as (keyof T)[];
  const keysB = Object.keys(b as object) as (keyof T)[];
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!Object.is(a[key], b[key])) return false;
  }
  return true;
}

// Hook

export function useDraftState<T>(
  seed: T | undefined,
  seedKey: Key | null,
  opts?: UseDraftStateOpts<T>,
): UseDraftStateResult<T> {
  // Determine the initial value at mount time.
  // - If seed is defined: use it.
  // - Else if opts.initial is defined: use it (isNew path).
  // - Else: unseeded.
  const initialValue = seed !== undefined ? seed : opts?.initial;

  const [s, setS] = useState<DraftStateInternal<T>>(() => ({
    appliedKey: initialValue !== undefined ? seedKey : UNSEEDED,
    draft: initialValue,
    pristine: initialValue,
  }));

  // Render-phase reset (React's "adjust state during render" pattern).
  //
  // Fires when the entity identity changes (appliedKey !== seedKey) AND data
  // for the new key is available (seed !== undefined). The setS call during
  // render causes React to abort this render and immediately re-render with
  // the new state — the caller observes only the final, correct draft.
  //
  // The guard is on `seedKey` (entity identity), NOT on `seed` (data
  // identity), so SSE cache patches (new object, same key) are ignored
  // structurally — they never cause a reseed.
  if (s.appliedKey !== seedKey && seed !== undefined) {
    setS({ appliedKey: seedKey, draft: seed, pristine: seed });
  }

  // setDraft — partial update over draft
  //
  // Object drafts (typeof draft === 'object', non-null): shallow-merge, as
  // before. Primitive drafts (string/number/boolean) and the unseeded case
  // (draft === undefined): replace wholesale — merging would spread a
  // primitive into an indexed object (`{ ...'Hi' }` → `{ 0: 'H', 1: 'i' }`),
  // corrupting the draft on the very first keystroke. See PR #1046 review B1.
  const setDraft = useCallback((partial: Partial<T>) => {
    setS((prev) => ({
      ...prev,
      // The guard must stay inline in the ternary test (not hoisted to a
      // `const`) — TS's control-flow narrowing of `prev.draft` from
      // `T | undefined` down to `T` in the true branch depends on the
      // `!== undefined` check appearing directly in the condition.
      draft: (prev.draft !== undefined && typeof prev.draft === 'object' && prev.draft !== null)
        ? { ...prev.draft, ...partial }
        : (partial as T),
    }));
  }, []);

  // reseed — server-ack path
  // Sets draft AND pristine, clearing isDirty. Does NOT change appliedKey,
  // so subsequent SSE churn for the same key continues to be blocked.
  const reseed = useCallback((value: T) => {
    setS((prev) => ({ ...prev, draft: value, pristine: value }));
  }, []);

  // reseedIfUnchanged — snapshot-guard reseed
  //
  // If the CURRENT draft still shallow-equals the `snapshot` captured at
  // mutation dispatch time, adopt the value fully (draft + pristine). If the
  // user typed since dispatch, keep the draft but still re-baseline `pristine`
  // to the server value, so isDirty compares the in-flight edit against what
  // the server now holds (a no-op here would leave a stale pre-save baseline
  // and let isDirty read false after an in-flight revert).
  //
  // The guard uses setS's functional updater so the comparison is always
  // against the current state — closing over a stale `s.draft` ref would
  // produce false negatives for rapid edits.
  const reseedIfUnchanged = useCallback((value: T, snapshot: T | undefined) => {
    setS((prev) =>
      shallowEqual(prev.draft, snapshot)
        ? { ...prev, draft: value, pristine: value }
        : { ...prev, pristine: value },
    );
  }, []);

  // isDirty — derived from shallow-equal of draft vs pristine
  const isDirty = !shallowEqual(s.draft, s.pristine);

  return {
    draft: s.draft,
    setDraft,
    reseed,
    reseedIfUnchanged,
    isDirty,
  };
}
